import React, { useMemo, useState } from "react";
import { Link } from "@/components/LocaleLink";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ArrowLeft, RotateCcw, ArrowRight, Check, Info, ShieldCheck, Clock, Activity, Target, Package, FileDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { finderMeta, questions, recommend } from "@/data/forloebFinder";
import { getProduct, getCompare } from "@/data/indsatser";
import { useSeo } from "@/hooks/useSeo";
import { trackEvent, EVENTS } from "@/lib/analytics";
import { ProductPdfButton } from "@/components/LazyProductPdf";
import { Eyebrow } from "@/components/Reveal";

const FindForloeb = () => {
  const { lang } = useLang();
  const m = finderMeta[lang];
  const qs = questions[lang];
  const reduce = useReducedMotion();
  useSeo(m.seoTitle, m.seoDesc);

  // phase: "intro" | 0..3 (question index) | "result"
  const [phase, setPhase] = useState("intro");
  const [answers, setAnswers] = useState({});

  const result = useMemo(() => (phase === "result" ? recommend(answers) : null), [phase, answers]);

  const anim = reduce ? {} : { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -12 }, transition: { duration: 0.35 } };

  const start = () => {
    trackEvent(EVENTS.FINDER_STARTED, { lang });
    setPhase(0);
  };

  const choose = (qIndex, value) => {
    const code = qs[qIndex].code;
    const next = { ...answers, [code]: value };
    setAnswers(next);
    trackEvent(EVENTS.FINDER_STEP, { step: qIndex + 1, category: value, lang });
    if (qIndex < qs.length - 1) {
      setPhase(qIndex + 1);
    } else {
      const r = recommend(next);
      trackEvent(EVENTS.FINDER_COMPLETED, { lang });
      trackEvent(EVENTS.FINDER_RECOMMENDATION, { recommended: r.recommended, alternative: r.alternative || "", lang });
      setPhase("result");
    }
  };

  const back = () => {
    if (phase === "result") setPhase(qs.length - 1);
    else if (typeof phase === "number" && phase > 0) setPhase(phase - 1);
    else setPhase("intro");
  };

  const restart = () => {
    trackEvent(EVENTS.FINDER_RESTART, { lang });
    setAnswers({});
    setPhase("intro");
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-32 pb-24 lg:pt-40">
        {/* INTRO */}
        {phase === "intro" && (
          <motion.div {...anim} key="intro">
            <Eyebrow>{m.kicker}</Eyebrow>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl text-[#1A2E24] leading-[1.05]">{m.title}</h1>
            <p className="mt-6 text-lg text-[#3B5245] leading-relaxed">{m.intro}</p>
            <div className="mt-8 flex items-start gap-3 rounded-2xl bg-white border border-[#EAE5D9] p-5" data-testid="finder-legal-start">
              <Info className="h-5 w-5 text-[#C47C5D] shrink-0 mt-0.5" />
              <p className="text-sm text-[#5C7567] leading-relaxed">{m.legalStart}</p>
            </div>
            <button
              onClick={start}
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-[#2D5A3F] text-white px-8 py-4 font-medium hover:bg-[#1A2E24] transition-colors duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#8EA694]/40"
              data-testid="finder-start-btn"
            >
              {m.startBtn}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </motion.div>
        )}

        {/* QUESTIONS */}
        {typeof phase === "number" && (
          <div data-testid={`finder-step-${phase + 1}`}>
            {/* progress */}
            <div className="mb-8">
              <div className="flex items-center justify-between text-sm text-[#5C7567] mb-2">
                <span>{m.step} {phase + 1} {m.of} {qs.length}</span>
                <span>{Math.round(((phase + 1) / qs.length) * 100)}%</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-[#EAE5D9] overflow-hidden">
                <motion.div
                  className="h-full bg-[#2D5A3F]"
                  initial={false}
                  animate={{ width: `${((phase + 1) / qs.length) * 100}%` }}
                  transition={reduce ? { duration: 0 } : { duration: 0.4 }}
                />
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div {...anim} key={phase}>
                <fieldset>
                  <legend className="font-display text-3xl sm:text-4xl text-[#1A2E24] leading-tight mb-7">
                    {qs[phase].q}
                  </legend>
                  <div className="grid gap-3" role="radiogroup" aria-label={qs[phase].q}>
                    {qs[phase].options.map((o) => {
                      const selected = answers[qs[phase].code] === o.value;
                      return (
                        <button
                          key={o.value}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          onClick={() => choose(phase, o.value)}
                          className={`group flex items-center justify-between gap-4 text-left rounded-2xl border p-5 sm:p-6 transition-colors duration-200 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#8EA694]/40 ${
                            selected ? "border-[#2D5A3F] bg-[#EAE5D9]" : "border-[#D3CEC0] bg-white hover:border-[#8EA694] hover:bg-white"
                          }`}
                          data-testid={`finder-option-${o.value}`}
                        >
                          <span className="text-[#1A2E24] font-medium leading-snug">{o.label}</span>
                          <span className={`flex items-center justify-center h-7 w-7 rounded-full border shrink-0 transition-colors ${selected ? "bg-[#2D5A3F] border-[#2D5A3F] text-white" : "border-[#D3CEC0] text-transparent group-hover:border-[#8EA694]"}`}>
                            <Check className="h-4 w-4" />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </fieldset>

                <div className="mt-8 flex items-center gap-3">
                  <button onClick={back} className="inline-flex items-center gap-2 rounded-full border border-[#D3CEC0] text-[#3B5245] px-5 py-2.5 font-medium hover:border-[#2D5A3F] hover:text-[#2D5A3F] transition-colors duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#8EA694]/40" data-testid="finder-back-btn">
                    <ArrowLeft className="h-4 w-4" /> {m.back}
                  </button>
                  <button onClick={restart} className="inline-flex items-center gap-2 rounded-full text-[#5C7567] px-4 py-2.5 font-medium hover:text-[#2D5A3F] transition-colors duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#8EA694]/40" data-testid="finder-restart-btn">
                    <RotateCcw className="h-4 w-4" /> {m.restart}
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        )}

        {/* RESULT */}
        {phase === "result" && result && (
          <ResultView lang={lang} m={m} result={result} onRestart={restart} onBack={back} reduce={reduce} />
        )}
      </div>
    </div>
  );
};

const ResultView = ({ lang, m, result, onRestart, onBack, reduce }) => {
  const product = getProduct(lang, result.recommended);
  const compareRow = getCompare(lang).rows.find((r) => r.slug === result.recommended);
  const altProduct = result.alternative ? getProduct(lang, result.alternative) : null;
  const reason = result.ambiguous ? m.ambiguousReason : (m.reasons[result.recommended] || "");
  const { Icon } = product;

  const kat = (result.needsSummary || []).join(",");
  const contactHref = `/kontakt?forloeb=${result.recommended}&kilde=forloebsvaelger${result.alternative ? `&alt=${result.alternative}` : ""}${kat ? `&kat=${encodeURIComponent(kat)}` : ""}`;

  const anim = reduce ? {} : { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4 } };

  const meta = [
    { Icon: Clock, label: m.durationLabel, value: product.duration },
    { Icon: Activity, label: m.intensityLabel, value: compareRow?.intensity },
    { Icon: Target, label: m.purposeLabel, value: compareRow?.purpose },
  ];

  return (
    <motion.div {...anim} data-testid="finder-result">
      <Eyebrow>{m.resultKicker}</Eyebrow>

      <div className="mt-5 rounded-3xl overflow-hidden border border-black/5 bg-white">
        <div className="p-7 sm:p-9" style={{ backgroundColor: product.accent }}>
          <div className="flex items-center gap-4 text-white">
            <span className="flex items-center justify-center h-14 w-14 rounded-2xl bg-white/15">
              <Icon className="h-7 w-7" strokeWidth={1.5} />
            </span>
            <div>
              <h1 className="font-display text-3xl sm:text-4xl leading-tight" data-testid="finder-recommended-name">{product.name}</h1>
              {product.subtitle && <p className="text-white/85">– {product.subtitle}</p>}
            </div>
          </div>
        </div>

        <div className="p-7 sm:p-9">
          <h2 className="text-xs uppercase tracking-[0.2em] text-[#5C7567] mb-2">{m.reasonLabel}</h2>
          <p className="text-lg text-[#1A2E24] leading-relaxed">{reason}</p>

          <div className="grid sm:grid-cols-3 gap-4 mt-7">
            {meta.map((mm) => (
              <div key={mm.label} className="rounded-2xl bg-[#F7F5F0] border border-[#EAE5D9] p-5">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#5C7567] mb-1.5"><mm.Icon className="h-4 w-4" /> {mm.label}</div>
                <p className="text-[#1A2E24] font-medium leading-snug">{mm.value}</p>
              </div>
            ))}
          </div>

          <div className="mt-7">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#5C7567] mb-3"><Package className="h-4 w-4" /> {m.deliverablesLabel}</div>
            <ul className="grid sm:grid-cols-2 gap-2">
              {product.deliverables.map((it) => (
                <li key={it} className="flex items-start gap-2.5 text-[#3B5245]">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: product.accent }} /> {it}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={`/indsatser/${product.slug}`} className="inline-flex items-center gap-2 rounded-full border border-[#2D5A3F] text-[#2D5A3F] px-5 py-2.5 font-medium hover:bg-[#2D5A3F] hover:text-white transition-colors duration-300" data-testid="finder-see-product">
              {m.seeProduct} <ArrowRight className="h-4 w-4" />
            </Link>
            <div className="[&>a]:!bg-[#EAE5D9] [&>a]:!text-[#1A2E24]">
              <ProductPdfButton product={product} onDownload={() => trackEvent(EVENTS.FINDER_PDF_DOWNLOAD, { slug: product.slug, lang })} />
            </div>
          </div>
        </div>
      </div>

      {/* Alternative */}
      {altProduct && (
        <div className="mt-5 rounded-3xl border border-dashed border-[#D3CEC0] bg-white/60 p-7" data-testid="finder-alternative">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#5C7567] mb-2">
            <altProduct.Icon className="h-4 w-4" /> {m.alternativeLabel}
          </div>
          <h3 className="font-display text-2xl text-[#1A2E24]">{altProduct.name}</h3>
          <p className="mt-2 text-[#3B5245] leading-relaxed">{m.alternativeExplain}</p>
          <Link to={`/indsatser/${altProduct.slug}`} className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-[#2D5A3F]">
            {m.seeProduct} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}

      {/* Disclaimer */}
      <div className="mt-6 flex items-start gap-3 rounded-2xl bg-[#EAE5D9] p-5" data-testid="finder-disclaimer">
        <ShieldCheck className="h-5 w-5 text-[#2D5A3F] shrink-0 mt-0.5" />
        <p className="text-sm text-[#3B5245] leading-relaxed">{m.disclaimer}<br /><span className="text-[#5C7567]">{m.legalResult}</span></p>
      </div>

      {/* CTAs */}
      <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <Link
          to={contactHref}
          onClick={() => trackEvent(EVENTS.FINDER_CONTACT_CLICK, { recommended: result.recommended, alternative: result.alternative || "", lang })}
          className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#2D5A3F] text-white px-7 py-4 font-medium hover:bg-[#1A2E24] transition-colors duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#8EA694]/40"
          data-testid="finder-contact-cta"
        >
          {m.primaryCta}
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
        <button onClick={onBack} className="inline-flex items-center justify-center gap-2 rounded-full border border-[#D3CEC0] text-[#3B5245] px-5 py-4 font-medium hover:border-[#2D5A3F] hover:text-[#2D5A3F] transition-colors duration-300" data-testid="finder-result-back">
          <ArrowLeft className="h-4 w-4" /> {m.back}
        </button>
        <button onClick={onRestart} className="inline-flex items-center justify-center gap-2 rounded-full text-[#5C7567] px-5 py-4 font-medium hover:text-[#2D5A3F] transition-colors duration-300" data-testid="finder-result-restart">
          <RotateCcw className="h-4 w-4" /> {m.restart}
        </button>
      </div>
    </motion.div>
  );
};

export default FindForloeb;
